import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Search, Inbox, Archive, ArchiveRestore, PenSquare, X, Building2,
} from 'lucide-react';
import { theme } from '../shared/theme';
import MessageThread from '../shared/MessageThread';
import { useLanguage } from '../../components/LanguageContext';
import {
  PmThread, PmMessage, PmRecipient,
  fetchThreads, fetchThread, replyToThread, markThreadRead, setThreadStatus,
  startThread, fetchRecipients, messageTime, pollWhileActive,
} from '../shared/messaging';
import '../shared/portals.css';

// ============================================================
// AdminInbox — the management side of the owner conversation.
//
// A work queue, not a mailbox: open conversations first, newest activity at
// the top, unread marked on the row so the team can see at a glance who is
// waiting. Answered threads are archived out of the way rather than deleted,
// and any reply from the owner pulls one back in (see pmMessaging.js).
//
// The same MessageThread the owner reads is used for the conversation itself,
// with the sides flipped — so what the manager types is laid out exactly as
// the owner will receive it.
// ============================================================

const LIST_POLL_MS = 20000;
const THREAD_POLL_MS = 10000;

type Filter = 'open' | 'archived' | 'all';

const initials = (name: string) =>
  (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

interface Props {
  /** Shown as the author name on outgoing messages. */
  staffName?: string;
  /** Lets the parent mirror the unread count onto its own tab. */
  onUnreadChange?: (n: number) => void;
  /**
   * True while a conversation is open. The console hides its own tab bar
   * then: the thread brings its own header, and two stacked headers over one
   * conversation is a row of chrome nobody asked for.
   */
  onFocusChange?: (focused: boolean) => void;
}

const AdminInbox: React.FC<Props> = ({ staffName, onUnreadChange, onFocusChange }) => {
  const { t, language } = useLanguage();
  const locale = language === 'de' ? 'de-DE' : 'en-GB';

  const [filter, setFilter] = useState<Filter>('open');
  const [q, setQ] = useState('');
  const [threads, setThreads] = useState<PmThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [compose, setCompose] = useState(false);
  const listInFlight = useRef(false);

  const loadList = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (listInFlight.current) return;
    listInFlight.current = true;
    if (!opts.silent) setLoading(true);
    try {
      const { threads: rows, unread } = await fetchThreads(filter, q);
      setThreads(rows || []);
      setError(null);
      onUnreadChange?.(unread || 0);
    } catch (e: any) {
      if (!opts.silent) setError(e?.message || t('inbLoadFailed'));
    } finally {
      listInFlight.current = false;
      setLoading(false);
    }
  }, [filter, q, onUnreadChange, t]);

  useEffect(() => { loadList(); }, [loadList]);

  // Tell the console whether a conversation (or the recipient picker) has the
  // screen. Reported from an effect rather than the click handlers so it can
  // never disagree with what is actually rendered.
  useEffect(() => { onFocusChange?.(!!openId || compose); }, [openId, compose, onFocusChange]);
  useEffect(() => () => { onFocusChange?.(false); }, [onFocusChange]);

  // Keep the queue live while it is on screen. Paused behind an open thread:
  // that view polls itself, and two pollers against a cold backend is one
  // more than the inbox needs. Paused again once the desk is unattended —
  // see pollWhileActive.
  useEffect(() => {
    if (openId) return;
    return pollWhileActive(() => loadList({ silent: true }), LIST_POLL_MS);
  }, [openId, loadList]);

  if (openId) {
    return (
      <AdminThreadView
        threadId={openId}
        staffName={staffName}
        locale={locale}
        onBack={() => { setOpenId(null); loadList({ silent: true }); }}
      />
    );
  }

  if (compose) {
    return (
      <RecipientPicker
        onCancel={() => setCompose(false)}
        onPicked={(threadId) => { setCompose(false); setOpenId(threadId); }}
      />
    );
  }

  const tabs: { key: Filter; label: string }[] = [
    { key: 'open', label: t('inbFilterOpen') },
    { key: 'archived', label: t('inbFilterArchived') },
    { key: 'all', label: t('inbFilterAll') },
  ];

  return (
    <div className="pt-fade" style={{ padding: 14 }}>
      <div className="pm-board">
        {/* Filters and "new" on one line, as in the reference. */}
        <div className="pm-board-tabs">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setFilter(tb.key)}
              className={`pm-board-tab ${filter === tb.key ? 'is-on' : ''}`}
            >
              {tb.label}
            </button>
          ))}
          <button onClick={() => setCompose(true)} className="pm-board-new">
            <PenSquare size={14} /> {t('inbNew')}
          </button>
        </div>

        <div className="pm-board-search">
          <Search size={17} color="rgba(255,255,255,0.45)" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('inbSearch')} />
          {q && (
            <button
              onClick={() => setQ('')}
              aria-label={t('inbClear')}
              style={{ background: 'none', border: 'none', padding: 0, minHeight: 0, color: 'rgba(255,255,255,0.45)' }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {error && <div className="pm-chat-error" style={{ margin: '0 0 10px' }}>{error}</div>}

        {loading && !threads.length && (
          <>
            <div className="pm-board-skel" />
            <div className="pm-board-skel" />
            <div className="pm-board-skel" />
          </>
        )}

        {!loading && !threads.length && (
          <div className="pm-board-empty">
            <Inbox size={30} color={theme.gold} />
            <div className="pm-board-empty-title">{t('inbEmptyTitle')}</div>
            <div className="pm-board-empty-text">{t('inbEmptyText')}</div>
          </div>
        )}

        {threads.map((th) => {
          const unread = th.unreadForAdmin > 0;
          const last = th.lastMessage;
          const tenant = th.kind === 'tenant';
          return (
            <button
              key={th.id}
              onClick={() => setOpenId(th.id)}
              className={`pt-tap pm-board-row ${unread ? 'is-unread' : ''}`}
            >
              {/* The spine carries the role in colour AND in words, so the
                  colour never has to be learned before it is useful. The
                  same building generates both, and "when is the AC fixed?"
                  has a different answer depending on who is asking. */}
              <span className="pm-board-spine" style={{ background: tenant ? '#4aa3f0' : theme.gold }}>
                {tenant ? t('eaTenant') : t('eaPropertyOwner')}
              </span>
              <span className="pm-board-main">
                <span className="pm-board-name">{th.participantName}</span>
                {th.participantEmail && <span className="pm-board-sub">{th.participantEmail}</span>}
                <span className="pm-board-preview">
                  {last
                    ? (<>{last.from === 'admin' && <b>{t('inbYouPrefix')} </b>}{last.text}</>)
                    : t('inbNoMessagesYet')}
                </span>
                <span className="pm-board-when">{messageTime(th.updatedAt, locale)}</span>
              </span>
              <span className="pm-board-side">
                {unread && (
                  <span className="pm-board-pill pm-board-pill--unread">
                    {th.unreadForAdmin} {t('inbNewCount')}
                  </span>
                )}
                <span className="pm-board-pill" style={{ background: '#F6F1E4', color: '#6B5320' }}>
                  {routeLabel(th, locale.startsWith('de'))}
                </span>
                <span className={`pm-board-pill ${th.status === 'archived' ? 'pm-board-pill--archived' : 'pm-board-pill--open'}`}>
                  {th.status === 'archived' ? t('inbFilterArchived') : t('inbStatusOpen')}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// What the customer chose and who it is routed to, e.g. "Real estate → g.joloc".
// The server only returns threads this admin may see; the label says why.
function routeLabel(th: PmThread, de: boolean): string {
  const who = (th.assignedTo || []).map((e) => e.split('@')[0]).join(', ');
  const topic = th.topic === 'realestate' ? (de ? 'Immobilie' : 'Real estate')
    : th.topic === 'pm' ? (de ? 'Hausverwaltung' : 'Property mgmt')
    : (de ? 'Kein Thema' : 'No topic');
  return who ? `${topic} → ${who}` : `${topic} · ${de ? 'alle' : 'all admins'}`;
}

// ---------------- one conversation ----------------

const AdminThreadView: React.FC<{
  threadId: string;
  staffName?: string;
  locale: string;
  onBack: () => void;
}> = ({ threadId, staffName, locale, onBack }) => {
  const { t } = useLanguage();
  const [thread, setThread] = useState<PmThread | null>(null);
  const [messages, setMessages] = useState<PmMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (!opts.silent) setLoading(true);
    try {
      const res = await fetchThread(threadId);
      setThread(res.thread);
      setMessages(res.messages || []);
      setError(null);
      if (res.thread && res.thread.unreadForAdmin > 0) {
        markThreadRead(threadId).catch(() => { /* the badge, not the message */ });
      }
    } catch (e: any) {
      if (!opts.silent) setError(e?.message || t('inbLoadFailed'));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [threadId, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => pollWhileActive(() => load({ silent: true }), THREAD_POLL_MS, 10 * 60 * 1000), [load]);

  const send = useCallback(async (body: string) => {
    const optimistic: PmMessage = {
      id: `tmp-${Date.now()}`,
      threadId,
      body,
      from: 'admin',
      authorName: staffName || 'Management',
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setSending(true);
    try {
      const { message, thread: fresh } = await replyToThread(threadId, body);
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? message : m)));
      setThread(fresh);
      setError(null);
    } catch (e: any) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setError(e?.message || t('inbSendFailed'));
    } finally {
      setSending(false);
    }
  }, [threadId, staffName, t]);

  const toggleArchive = useCallback(async () => {
    if (!thread) return;
    const next = thread.status === 'archived' ? 'open' : 'archived';
    setThread({ ...thread, status: next });
    try { await setThreadStatus(threadId, next); }
    catch (e: any) { setError(e?.message || t('inbSendFailed')); }
  }, [thread, threadId, t]);

  return (
    <MessageThread
      title={thread?.participantName || t('inbConversation')}
      subtitle={thread ? [thread.participantEmail, routeLabel(thread, locale.startsWith('de'))].filter(Boolean).join(' · ') : undefined}
      meSide="admin"
      messages={messages}
      loading={loading}
      sending={sending}
      error={error}
      emptyTitle={t('inbThreadEmptyTitle')}
      emptyText={t('inbThreadEmptyText')}
      placeholder={t('inbPlaceholder')}
      sendLabel={t('omSend')}
      onSend={send}
      onBack={onBack}
      locale={locale}
      headerActions={(
        <button onClick={toggleArchive} className="pt-tap pm-chat-hbtn" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {thread?.status === 'archived' ? <ArchiveRestore size={14} /> : <Archive size={14} />}
          {thread?.status === 'archived' ? t('inbUnarchive') : t('inbArchive')}
        </button>
      )}
    />
  );
};

// ---------------- start a new conversation ----------------

const RecipientPicker: React.FC<{ onCancel: () => void; onPicked: (threadId: string) => void }> = ({ onCancel, onPicked }) => {
  const { t } = useLanguage();
  const [q, setQ] = useState('');
  const [owners, setOwners] = useState<PmRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchRecipients()
      .then((r) => { if (alive) { setOwners(r.owners || []); setError(null); } })
      .catch((e) => { if (alive) setError(e?.message || t('inbLoadFailed')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [t]);

  const open = async (o: PmRecipient) => {
    // An existing thread is reused rather than duplicated — one conversation
    // per owner is the whole point of the model.
    if (o.threadId) return onPicked(o.threadId);
    setBusyId(o.id);
    try {
      const { thread } = await startThread({ id: o.id, name: o.name, email: o.email, kind: o.kind });
      onPicked(thread.id);
    } catch (e: any) {
      setError(e?.message || t('inbSendFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const filtered = owners.filter((o) => `${o.name} ${o.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="pt-fade" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <button onClick={onCancel} className="pt-tap" style={{ background: '#fff', border: `1px solid ${theme.line}`, borderRadius: 10, padding: '7px 12px', minHeight: 0, fontSize: 12.5, fontWeight: 700, color: theme.slate, cursor: 'pointer' }}>
          {t('inbCancel')}
        </button>
        <div style={{ fontSize: 15.5, fontWeight: 800, color: theme.ink }}>{t('inbNewTitle')}</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${theme.line}`, borderRadius: 12, padding: '9px 12px', marginBottom: 12 }}>
        <Search size={17} color={theme.slateLight} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('inbSearchOwners')}
          style={{ border: 'none', outline: 'none', fontSize: 14.5, flex: 1, background: 'transparent', padding: 0, width: 'auto' }}
        />
      </div>

      {error && <div className="pm-chat-error" style={{ margin: '0 0 10px' }}>{error}</div>}
      {loading && <div className="pt-skel" style={{ height: 64 }} />}

      {!loading && !filtered.length && (
        <div className="pm-chat-empty">
          <Building2 size={28} color={theme.gold} />
          <div className="pm-chat-empty-title">{t('inbNoOwners')}</div>
          <div className="pm-chat-empty-text">{t('inbNoOwnersText')}</div>
        </div>
      )}

      {filtered.map((o) => (
        <button key={o.id} onClick={() => open(o)} disabled={busyId === o.id} className="pt-tap pm-inbox-row">
          <div className="pm-inbox-avatar" style={o.kind === 'tenant' ? { background: theme.infoBg, color: theme.info } : undefined}>
            {initials(o.name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span className="pm-inbox-name">{o.name}</span>
              <span className="pm-inbox-kind" style={o.kind === 'tenant'
                ? { background: theme.infoBg, color: theme.info }
                : { background: '#fdf6e6', color: '#8a6d1f' }}>
                {o.kind === 'tenant' ? t('eaTenant') : t('eaPropertyOwner')}
              </span>
            </div>
            <div className="pm-inbox-preview">{o.email || t('inbNoEmail')}</div>
          </div>
          <div className="pm-inbox-meta">
            {o.threadId && <span className="pm-inbox-when">{t('inbHasThread')}</span>}
            {o.unread > 0 && <span className="pm-inbox-badge">{o.unread}</span>}
          </div>
        </button>
      ))}
    </div>
  );
};

export default AdminInbox;
